import { LinearProgressBarß } from '@otter-ai/den-components';
import { FILE_STATES } from '@rpldy/shared';
import { useEffect, useState } from 'react';
import { SECOND_MS } from '@/constants/time';
import { useBreakpoint } from '@/features/left-nav/Misc/screen-size';
import { useSpeechTranscriptionStatus } from '../hooks/helperHooks';
import type { SpeechTranscriptionStatus, UploadItem } from '../types/types';
import { UploadStatus } from './UploadStatus';

const TRANSCRIPTION_POLL_INTERVAL = 2 * SECOND_MS;
const MEGABYTE = 1024 * 1024;

export function UploadFileRow({ item }: { item: UploadItem }) {
  const { getSpeechTranscriptionStatus } = useSpeechTranscriptionStatus();
  const [transcriptionStatus, setTranscriptionStatus] =
    useState<SpeechTranscriptionStatus | null>(null);
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'mobile';

  // Poll transcription status every 2 seconds
  useEffect(() => {
    if (!item.otid) return;

    const intervalId = setInterval(() => {
      const status = getSpeechTranscriptionStatus(item.otid);
      setTranscriptionStatus(status);

      // Stop polling when transcription is complete
      if (status?.isTranscriptionComplete) {
        clearInterval(intervalId);
      }
    }, TRANSCRIPTION_POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [item.otid, getSpeechTranscriptionStatus]);

  // Calculate progress and label
  const progressPercentage = transcriptionStatus
    ? transcriptionStatus.transcriptionProgress
    : item.state === FILE_STATES.UPLOADING ||
        item.state === FILE_STATES.FINISHED
      ? Math.floor(item.completed)
      : 0;

  const progressLabel = transcriptionStatus
    ? transcriptionStatus.isTranscribing
      ? `Transcribing ${progressPercentage}%`
      : ''
    : `Uploading ${progressPercentage}%`;

  // Format file size in MB
  const fileSizeInMB = (item.file.size / MEGABYTE).toFixed(2);

  // Format remaining time
  const remainingTimeDisplay = transcriptionStatus?.isTranscriptionComplete
    ? 'Done'
    : transcriptionStatus?.remainingMinutes
      ? `${transcriptionStatus?.remainingMinutes}m left`
      : '...';

  return (
    <div
      data-testid="upload-file-row"
      className="w-full h-12 flex flex-row items-center flex-start text-sm border border-palette-gray-200 font-semibold"
    >
      {/* File Name */}
      <div
        data-testid="upload-file-name"
        className="text-left flex-[8] text-palette-gray-950 truncate p-2 pl-6"
      >
        {item.file.name}
      </div>

      {/* File Size */}
      <div className="text-center flex-[5] text-subtle p-2">
        {fileSizeInMB} MB
      </div>

      {/* Progress Section */}
      <div className="text-center flex flex-col flex-[5] justify-center text-xs p-2">
        {!isMobile && (
          <div
            data-testid="upload-file-row-progress-label"
            className="flex justify-between items-center mb-1 text-subtle"
          >
            <span>{progressLabel}</span>
            {transcriptionStatus && (
              <span className="text-palette-gray-600 hidden md:inline">
                {remainingTimeDisplay}
              </span>
            )}
          </div>
        )}

        {isMobile &&
          (!transcriptionStatus ? (
            <UploadStatus item={item} isMobile={isMobile} />
          ) : (
            <span className="text-palette-gray-600 text-right">
              {remainingTimeDisplay}
            </span>
          ))}
        <LinearProgressBarß
          data-testid="upload-file-row-progress-bar"
          className="h-1.5"
          value={progressPercentage}
        />
      </div>

      {/* Status */}
      {!isMobile && (
        <div className="text-right flex-[3] p-2 font-semibold">
          <UploadStatus item={item} isMobile={isMobile} />
        </div>
      )}
    </div>
  );
}
